import { isKilnActive } from '../../pages/masterdata/kilnActivity';
import { formatReportingPeriod } from '../../pages/emissionReports/emissionReportRow';
import { ReportStatus, ReportStatusLabels } from '../../types/reports';
import type { EnvironmentalData } from '../../types/reports';
import type { PlantKiln } from '../../types/entities';

/**
 * Die Auswahlregeln der Zuordnungskaskade (Sketch 019) als REINE Funktionen -- ohne React, ohne
 * API-Zugriff, damit sie unter `environment: 'node'` pruefbar sind.
 *
 * Werk zuerst, danach Ofen und Meldung; beide haengen am Werk, nicht voneinander. Gefiltert wird
 * so scharf wie moeglich, damit am Ende oft nur EINE Entscheidung uebrig bleibt -- und wo keine
 * uebrig bleibt, wird gesetzt statt gefragt.
 *
 * Was hier NICHT passiert: das Nachfiltern einer geladenen Seite. Werke (Anlegerecht), Oefen
 * (Werk + Anlegerecht) und Meldungen (Werk + Stand + Jahr) kommen bereits serverseitig gefiltert
 * an. Dieses Modul zieht die zwei Grenzen, die der Server nicht kennt: die Ofenaktivitaet am
 * Stichtag und die Zaehlung dessen, was dadurch herausfaellt.
 */

/** Was die Kaskade von einem Ofen braucht. */
export type KilnChoice = Pick<PlantKiln, 'id' | 'name' | 'activeFrom' | 'activeUntil'>;

/** Was die Kaskade von einer Umweltmeldung braucht. */
export type ReportChoice = Pick<EnvironmentalData, 'id' | 'reportingPeriod' | 'status'>;

export type SlotKind = 'fixed' | 'choose' | 'empty';

/**
 * Der Zustand eines Kaskadenfeldes:
 *
 * - `fixed`  -- genau eine Moeglichkeit. Der Wert wird GESETZT, nicht ausgewaehlt, und steht mit
 *               seiner Begruendung daneben ("einziger Ofen", "einzige offene Meldung 2026").
 * - `choose` -- mehrere Moeglichkeiten, es braucht eine Entscheidung.
 * - `empty`  -- keine Moeglichkeit. Der Leerfall bekommt einen SATZ, keine leere Liste.
 */
export type Slot<T> =
  | { kind: 'fixed'; value: T; reason: string }
  | { kind: 'choose'; options: T[] }
  | { kind: 'empty'; message: string };

export interface SlotTexts {
  /** Begruendung im Fall `fixed`. */
  singleReason: string;
  /** Satz im Fall `empty`. */
  emptyMessage: string;
}

/** Der Leerfallsatz des Ofenfeldes -- woertlich aus Sketch 019. */
export const NO_ACTIVE_KILN_MESSAGE = 'Kein Ofen dieses Werks war im Messzeitraum aktiv.';

/** Der Leerfallsatz des Meldungsfeldes -- woertlich aus Sketch 019 und D-14. */
export function noOpenReportMessage(year: number): string {
  return `Für ${year} gibt es zu diesem Werk keine offene Umweltmeldung. Erst anlegen, dann einlesen.`;
}

export interface KilnChoices {
  offered: KilnChoice[];
  /** Wie viele der uebergebenen Oefen die Aktivitaetsregel herausnimmt -- wird ANGEZEIGT. */
  filteredOutCount: number;
}

/** Der reine Datumsanteil (`2026-03-31T00:00:00+02:00` -> `2026-03-31`). */
function toIsoDate(value: string): string {
  return value.slice(0, 10);
}

/**
 * Angeboten werden die Oefen, die am LETZTEN TAG des Messzeitraums aktiv waren. Geprueft ueber
 * `isKilnActive` aus den Stammdaten -- dieselbe Regel, nach der das Backend die Oefen einer
 * Meldeperiode auswaehlt; hier bewusst nicht neu hergeleitet.
 *
 * Ohne Enddatum (der Parser hat keinen Messzeitraum erkannt) gibt es keinen Stichtag, an dem
 * geprueft werden koennte. Dann werden ALLE uebergebenen Oefen angeboten, statt keinen: die
 * Alternative waere ein Leerfallsatz, der eine Tatsache behauptet, die niemand geprueft hat.
 */
export function kilnChoices(
  kilns: readonly KilnChoice[],
  measurementRangeEnd: string | null
): KilnChoices {
  if (!measurementRangeEnd) {
    return { offered: [...kilns], filteredOutCount: 0 };
  }

  const onDate = toIsoDate(measurementRangeEnd);
  const offered = kilns.filter((kiln) => isKilnActive(kiln, onDate));

  return { offered, filteredOutCount: kilns.length - offered.length };
}

/**
 * Der Zusatz unter dem Ofenfeld. Gefilterte Oefen werden GEZAEHLT, nicht verschwiegen -- sonst
 * sucht der Bearbeiter einen Ofen, den er kennt, und findet ihn nicht (Sketch 019).
 */
export function filteredOutNote(count: number): string | null {
  if (count <= 0) {
    return null;
  }

  return count === 1
    ? '1 Ofen im Messzeitraum stillgelegt'
    : `${count} Öfen im Messzeitraum stillgelegt`;
}

/**
 * Das Jahr, aus dem die Meldung stammen muss: das Jahr des ENDDATUMS des Messzeitraums, nicht
 * das heutige. Ein im Januar 2027 eingelesener Bericht ueber Messungen aus 2026 gehoert an die
 * Meldung 2026.
 */
export function measurementYear(measurementRangeEnd: string | null): number | null {
  if (!measurementRangeEnd) {
    return null;
  }

  const year = Number(measurementRangeEnd.slice(0, 4));

  return Number.isInteger(year) ? year : null;
}

/**
 * Die Staende, in die ein Bericht eingelesen werden darf: Entwurf, In Bearbeitung und
 * Ueberarbeitung -- die Meldung liegt noch beim Melder. Eine eingereichte Meldung liegt zur
 * Pruefung beim Verband, eine freigegebene ist final; beide lehnt der Uebernehmen-Endpunkt ab
 * (`EmissionReportImportAcceptProcessor`). Die Kaskade laedt die Meldungen serverseitig mit genau
 * diesen Staenden.
 */
export const IMPORTABLE_REPORT_STATUSES: ReportStatus[] = [
  ReportStatus.DRAFT,
  ReportStatus.IN_PROGRESS,
  ReportStatus.REVISION_NEEDED,
];

/** Die offenen Umweltmeldungen des Jahres -- "offen" im Sinne von `IMPORTABLE_REPORT_STATUSES`. */
export function reportChoices(reports: readonly ReportChoice[], year: number): ReportChoice[] {
  return reports.filter(
    (report) =>
      IMPORTABLE_REPORT_STATUSES.includes(report.status) &&
      Number(report.reportingPeriod.slice(0, 4)) === year
  );
}

/**
 * Aus einer Angebotsmenge wird der Feldzustand: eine Moeglichkeit wird gesetzt, mehrere werden
 * zur Wahl gestellt, keine bekommt den Satz.
 */
export function resolveSlot<T>(options: readonly T[], texts: SlotTexts): Slot<T> {
  if (options.length === 0) {
    return { kind: 'empty', message: texts.emptyMessage };
  }

  if (options.length === 1) {
    return { kind: 'fixed', value: options[0], reason: texts.singleReason };
  }

  return { kind: 'choose', options: [...options] };
}

/** Ofenfeld: Angebot, Zustand und die Zahl der herausgefallenen Oefen in einem Zug. */
export function kilnSlot(
  kilns: readonly KilnChoice[],
  measurementRangeEnd: string | null
): { slot: Slot<KilnChoice>; filteredOutCount: number } {
  const { offered, filteredOutCount } = kilnChoices(kilns, measurementRangeEnd);

  return {
    slot: resolveSlot(offered, {
      // Nur wenn tatsaechlich etwas herausgefallen ist, ist "im Messzeitraum aktiv" die
      // Begruendung -- sonst ist es schlicht der einzige Ofen des Werks.
      singleReason: filteredOutCount > 0 ? 'einziger im Messzeitraum aktiver Ofen' : 'einziger Ofen',
      emptyMessage: NO_ACTIVE_KILN_MESSAGE,
    }),
    filteredOutCount,
  };
}

/** Meldungsfeld. */
export function reportSlot(reports: readonly ReportChoice[], year: number): Slot<ReportChoice> {
  return resolveSlot(reportChoices(reports, year), {
    singleReason: `einzige offene Meldung ${year}`,
    emptyMessage: noOpenReportMessage(year),
  });
}

/**
 * Anzeigename einer Meldung. Bewusst dieselbe Quelle wie die eingefrorene Anzeige der Liste
 * (`formatReportingPeriod`), damit "Umweltdaten 2026" nicht an zwei Stellen verschieden entsteht
 * -- nur speist sie hier der LEBENDE Wert (D-13: `LiveAssignmentNames.period`).
 */
export function reportLabel(report: ReportChoice): string {
  return formatReportingPeriod(report.reportingPeriod) ?? `Umweltdaten ${report.id}`;
}

/**
 * Beschriftung im Auswahlfeld. Der Stand steht nur dort dabei, wo wirklich gewaehlt wird: liegen
 * zwei offene Meldungen desselben Jahres vor, waeren sie sonst nicht zu unterscheiden.
 */
export function reportChoiceLabel(report: ReportChoice): string {
  return `${reportLabel(report)} (${ReportStatusLabels[report.status]})`;
}

export interface AssignmentState {
  /** IRI des Werks, `null` solange keines gewaehlt ist. */
  plant: string | null;
  /** IRI des Ofens -- im Fall `fixed` gesetzt, nicht gewaehlt. */
  plantKiln: string | null;
  /** IRI der Meldung -- im Fall `fixed` gesetzt, nicht gewaehlt. */
  environmentalData: string | null;
  /** Zustand des Ofenfeldes; `null` heisst: noch kein Werk gewaehlt, also noch nichts geladen. */
  kilnKind: SlotKind | null;
  /** Zustand des Meldungsfeldes; `null` wie oben. */
  reportKind: SlotKind | null;
}

/**
 * Gibt die Vorschau frei? Werk gewaehlt, Ofen und Meldung je gesetzt oder gewaehlt. Ein
 * `empty`-Feld macht die Zuordnung unvollstaendig -- auch dann, wenn im Formularwert noch ein
 * alter Verweis steht, der zur neuen Werkswahl gar nicht mehr passt.
 */
export function isAssignmentComplete(state: AssignmentState): boolean {
  if (state.plant === null || state.plant === '') {
    return false;
  }

  const kilnOk =
    (state.kilnKind === 'fixed' || state.kilnKind === 'choose') &&
    state.plantKiln !== null &&
    state.plantKiln !== '';
  const reportOk =
    (state.reportKind === 'fixed' || state.reportKind === 'choose') &&
    state.environmentalData !== null &&
    state.environmentalData !== '';

  return kilnOk && reportOk;
}
